import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import { Layout } from '../../components/layout/Layout';
import { PageHeader } from '../../components/layout/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { schoolSetup } from '../../modules/platform/api/onboarding';
import { showErrorToast, showSuccessToast } from '../../utils/errorHandler';
import { ROUTES } from '../../utils/constants';
import { Plus, X } from 'lucide-react';

interface ClassFormData {
  name: string;
  grade: string;
  section: string;
}

interface SchoolSetupFormData {
  grades: string[];
  classes: ClassFormData[];
}

export const SchoolSetup: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const {
    register,
    control,
    handleSubmit,
    watch: _watch,
    formState: { errors: _errors },
  } = useForm<SchoolSetupFormData>({
    defaultValues: {
      grades: [],
      classes: [],
    },
  });

  const { fields: gradeFields, append: appendGrade, remove: removeGrade } =
    useFieldArray<any>({
      control,
      name: 'grades' as const,
    });

  const { fields: classFields, append: appendClass, remove: removeClass } =
    useFieldArray({
      control,
      name: 'classes' as const,
    });

  const onSubmit = async (data: SchoolSetupFormData) => {
    setIsLoading(true);
    try {
      await schoolSetup(data);
      showSuccessToast('School setup completed successfully!');
      navigate(ROUTES.ADMIN_DASHBOARD);
    } catch (error) {
      showErrorToast(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Layout>
      <PageHeader
        title="School Setup"
        subtitle="Set up your school's grade and class structure"
      />

      <Card className="max-w-4xl">
        <CardHeader>
          <CardTitle>Grades and Classes</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Grades Section */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Grades</h3>
              <div className="space-y-2">
                {gradeFields.map((field, index) => (
                  <div key={field.id} className="flex gap-2">
                    <Input
                      {...register(`grades.${index}` as const, { required: 'Grade name is required' })}
                      placeholder="e.g., Grade 1"
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeGrade(index)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => appendGrade('' as any)}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Grade
                </Button>
              </div>
            </div>

            {/* Classes Section */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Classes</h3>
              <div className="space-y-4">
                {classFields.map((field, index) => (
                  <div key={field.id} className="p-4 border rounded-lg space-y-2">
                    <div className="flex gap-2">
                      <Input
                        label="Class Name"
                        {...register(`classes.${index}.name` as const, { required: 'Class name is required' })}
                        placeholder="e.g., Grade 1 - Section A"
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeClass(index)}
                        className="mt-6"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        label="Grade"
                        {...register(`classes.${index}.grade` as const, { required: 'Grade is required' })}
                        placeholder="e.g., Grade 1"
                      />
                      <Input
                        label="Section"
                        {...register(`classes.${index}.section` as const, { required: 'Section is required' })}
                        placeholder="e.g., A"
                      />
                    </div>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => appendClass({ name: '', grade: '', section: '' })}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Class
                </Button>
              </div>
            </div>

            <div className="flex gap-4 pt-4 border-t">
              <Button type="submit" isLoading={isLoading} className="flex-1">
                Complete Setup
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </Layout>
  );
};


